import type { FilterValue, SortDirection } from '@warehouse/shared';
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router';

import { DEFAULT_PAGE_SIZE, NULL_FILTER_SENTINEL, type ListState } from '../lib/list-query';

/**
 * `ListState` living in the URL.
 *
 * Why the URL and not `useState`: a warehouse supervisor pastes "the filtered
 * list of blocked bins in area A" into Slack, and the back button after opening
 * a row must return to the same page/sort/search. Both come free once the state
 * is the query string.
 *
 * The URL shape mirrors the api's own params (`q`, `sort`, `dir`, `page`,
 * `pageSize`, `f_<key>`) so it reads sensibly to a human. Filters are prefixed
 * `f_` rather than `filters[…]` because that survives copy-paste through chat
 * clients that mangle brackets.
 */

export interface UseListStateOptions {
  defaultSort?: string;
  defaultDir?: SortDirection;
  defaultPageSize?: number;
  /** Namespace the params when two independent lists share one route. */
  prefix?: string;
}

export interface UseListStateResult {
  state: ListState;
  setState: (next: ListState) => void;
  patch: (partial: Partial<ListState>) => void;
  setFilter: (key: string, value: FilterValue | undefined) => void;
  reset: () => void;
}

export function useListState(options: UseListStateOptions = {}): UseListStateResult {
  const { defaultSort, defaultDir, defaultPageSize = DEFAULT_PAGE_SIZE, prefix = '' } = options;
  const [searchParams, setSearchParams] = useSearchParams();

  const key = useCallback((name: string) => `${prefix}${name}`, [prefix]);

  const state = useMemo<ListState>(() => {
    const filters: Record<string, FilterValue> = {};
    const filterPrefix = `${prefix}f_`;
    for (const [name, raw] of searchParams.entries()) {
      if (!name.startsWith(filterPrefix)) continue;
      const filterKey = name.slice(filterPrefix.length);
      const value = decodeFilterValue(raw);
      const existing = filters[filterKey];
      if (existing === undefined) {
        filters[filterKey] = value;
      } else if (Array.isArray(existing)) {
        // Repeated params mean a multi-select — an IN (...) filter.
        filters[filterKey] = [...existing, value as string];
      } else {
        filters[filterKey] = [existing as string, value as string];
      }
    }

    const page = Number(searchParams.get(key('page')) ?? '1');
    const pageSize = Number(searchParams.get(key('pageSize')) ?? String(defaultPageSize));

    return {
      q: searchParams.get(key('q')) ?? undefined,
      sort: searchParams.get(key('sort')) ?? defaultSort,
      dir: (searchParams.get(key('dir')) as SortDirection | null) ?? defaultDir,
      page: Number.isFinite(page) && page > 0 ? page : 1,
      pageSize: Number.isFinite(pageSize) && pageSize > 0 ? pageSize : defaultPageSize,
      cursor: searchParams.get(key('cursor')) ?? undefined,
      ...(Object.keys(filters).length > 0 ? { filters } : {}),
    };
  }, [searchParams, key, prefix, defaultSort, defaultDir, defaultPageSize]);

  const setState = useCallback(
    (next: ListState) => {
      setSearchParams(
        (current) => {
          const params = new URLSearchParams(current);
          // Drop everything this hook owns, then re-add — otherwise a removed
          // filter lingers in the URL forever.
          for (const name of [...params.keys()]) {
            if (
              name === key('q') ||
              name === key('sort') ||
              name === key('dir') ||
              name === key('page') ||
              name === key('pageSize') ||
              name === key('cursor') ||
              name.startsWith(`${prefix}f_`)
            ) {
              params.delete(name);
            }
          }

          if (next.q?.trim()) params.set(key('q'), next.q.trim());
          // Only non-default sort reaches the URL, so the common case stays a
          // clean `/articles` rather than `/articles?sort=name&dir=asc`.
          const sortIsDefault = next.sort === defaultSort && (next.dir ?? defaultDir) === defaultDir;
          if (next.sort && !sortIsDefault) params.set(key('sort'), next.sort);
          if (next.dir && !sortIsDefault) params.set(key('dir'), next.dir);
          if (next.page && next.page > 1) params.set(key('page'), String(next.page));
          if (next.pageSize && next.pageSize !== defaultPageSize) params.set(key('pageSize'), String(next.pageSize));
          if (next.cursor) params.set(key('cursor'), next.cursor);

          for (const [name, value] of Object.entries(next.filters ?? {})) {
            if (value === undefined) continue;
            const paramName = `${prefix}f_${name}`;
            if (value === null) {
              params.append(paramName, NULL_FILTER_SENTINEL);
            } else if (Array.isArray(value)) {
              for (const item of value) params.append(paramName, String(item));
            } else if (String(value).length > 0) {
              params.append(paramName, String(value));
            }
          }

          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams, key, prefix, defaultSort, defaultDir, defaultPageSize],
  );

  const patch = useCallback(
    (partial: Partial<ListState>) => {
      setState({ ...state, ...partial });
    },
    [setState, state],
  );

  const setFilter = useCallback(
    (filterKey: string, value: FilterValue | undefined) => {
      const filters = { ...(state.filters ?? {}) };
      if (value === undefined) delete filters[filterKey];
      else filters[filterKey] = value;
      // A filter change always returns to page 1.
      setState({ ...state, filters, page: 1, cursor: undefined });
    },
    [setState, state],
  );

  const reset = useCallback(() => {
    setState({ page: 1, pageSize: defaultPageSize, sort: defaultSort, dir: defaultDir });
  }, [setState, defaultPageSize, defaultSort, defaultDir]);

  return { state, setState, patch, setFilter, reset };
}

function decodeFilterValue(raw: string): FilterValue {
  if (raw === NULL_FILTER_SENTINEL) return null;
  if (raw === 'true') return true;
  if (raw === 'false') return false;
  return raw;
}
